import * as React from "react";

/** Height deltas at or below this are browser chrome (URL bar collapse),
 *  not a software keyboard. Same floor Base UI and vaul use. */
const KEYBOARD_RESIZE_THRESHOLD = 60;

/**
 * The height, in px, of the software keyboard overlaying the BOTTOM of the
 * layout viewport — on browsers that keep the layout viewport full-size while
 * the keyboard is up (iOS Safari always; any browser in the default
 * `interactive-widget=resizes-visual` mode).
 *
 * `position: fixed; inset: 0` overlays size against the layout viewport, so
 * without this a bottom-anchored popup sits underneath the keyboard. Overlay
 * viewports apply the value as bottom padding, which lifts a bottom-stuck
 * popup into the visible region.
 *
 * On browsers where the layout viewport itself shrinks for the keyboard
 * (`resizes-content` — this app opts in via the viewport meta, honored by
 * Chromium 108+ and Firefox Android 133+), `window.innerHeight` shrinks with
 * it and this stays 0 — the compensation never doubles.
 */
export function useVisualViewportBottomInset(): number {
  const [inset, setInset] = React.useState(0);

  React.useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const measure = (): number => {
      // Pinch-zoom also shrinks the visual viewport; that is not an overlay
      // and must not push popups around.
      if (viewport.scale !== 1) return 0;
      const reducedHeight = window.innerHeight - viewport.height;
      if (reducedHeight <= KEYBOARD_RESIZE_THRESHOLD) return 0;
      // iOS scrolls the visual viewport up to reveal the focused input, so
      // the keyboard's top edge is offsetTop + height, not just height.
      const keyboardTop = Math.min(
        window.innerHeight,
        Math.max(0, viewport.offsetTop) + viewport.height,
      );
      return Math.max(0, Math.ceil(window.innerHeight - keyboardTop));
    };

    const update = () => setInset(measure());

    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    // Desktop-class browsers report window resizes without visualViewport
    // events; and on iOS the visual viewport can settle late after blur, so
    // the extra signal is a re-measure opportunity, not redundancy.
    window.addEventListener("resize", update);
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  return inset;
}
